/**
 * Area snapshot — the instant answer.
 *
 * The product promise is "understand any place in 30 seconds". Before this
 * module, the map opened with a hint ("Tap any glowing spot to see its
 * verdict") — the answer was one tap away. The snapshot puts it ON screen:
 * the moment the verdict layer is up, the user sees a real area, its
 * verdict, the top authored reason, the most important live signal near it,
 * and the freshness heartbeat — no tap required. Tapping a pin or searching
 * replaces it with the full verdict card (the snapshot dismisses itself).
 *
 * Area resolution, in order of how much the user has told us:
 *   1. Cached GPS fix (sessionStorage, consent already given in a prior
 *      geolocate tap this session) → nearest verdict within 8 km, framed
 *      as "Near you". We NEVER prompt for location here — that's the
 *      geolocate pill's job, permission-first.
 *   2. The city's first suggested signature area (recognisable, hand-picked
 *      in main-simple.ts SUGGESTED_IDS) — framed by city name.
 *
 * Signal honesty rules (same contract as incident-bubbles):
 *   - Deduped feed only, never raw items.
 *   - City-anchored items (is_city_anchored) are never presented as local.
 *   - The signal shows publisher + per-item published_at age and links out.
 *     We point at the source; we don't paraphrase or editorialise.
 *   - If the suburb has no nearby signal, say so: absence as absence.
 */

import { loadIncidents, type CityKey } from "../data/incidents-loader";
import type { DedupedIncident } from "../data/incident-dedup";
import type { Verdict, VerdictLevel } from "../data/verdicts";
import { feedHeartbeat, relativeAge } from "./freshness";
import { trustLineFor } from "./trust-row";
import { COLOR, LEVEL_COLORS, LEVEL_TAILS } from "./tokens";

const SNAPSHOT_ID = "hyperlocal-area-snapshot";
const STYLE_ID = "hyperlocal-area-snapshot-styles";
/** Body attribute set while the snapshot is visible — CSS elsewhere uses it
 *  to keep the intel-panel placeholder out of the same screen corner. */
const BODY_FLAG = "data-area-snapshot";

/** Verdict pill foregrounds — verdict colours themselves come from tokens. */
const LEVEL_FG: Record<VerdictLevel, string> = {
  green: "#eafff5",
  amber: "#1a1200",
  red: "#fff0f0",
};

/** A cached GPS fix older than this is not "near you" any more. Mirrors the
 *  15-minute max age geolocate.ts enforces for the same sessionStorage key —
 *  presenting an hours-old fix as the user's location would be a quiet lie. */
const FIX_MAX_AGE_MS = 15 * 60 * 1000;

export interface AreaSnapshotOpts {
  cityId: CityKey;
  cityLabel: string;
  verdicts: Verdict[];
  nearFn: (lng: number, lat: number, maxKm?: number) => Verdict | null;
  /** Hand-picked recognisable area ids for this city (first hit wins). */
  suggestedIds: string[];
  /** Open the full experience: fly the camera + showVerdict. */
  onExpand: (v: Verdict) => void;
}

interface ResolvedArea {
  v: Verdict;
  context: "near-you" | "city";
}

function readCachedFix(): [number, number] | null {
  try {
    const raw = sessionStorage.getItem("hyperlocal.geo.lastFix");
    if (!raw) return null;
    const o = JSON.parse(raw);
    // Age-gate the fix (o.t is the capture timestamp). geolocate.ts already
    // refuses fixes older than 15 minutes; honouring the same window here
    // keeps "Near you" meaning *now*, not "where you were this morning".
    if (typeof o?.t === "number" && Date.now() - o.t > FIX_MAX_AGE_MS) {
      return null;
    }
    if (o && typeof o.lng === "number" && typeof o.lat === "number") {
      return [o.lng, o.lat];
    }
  } catch {
    /* sessionStorage blocked — fall through to city default */
  }
  return null;
}

function resolveArea(opts: AreaSnapshotOpts): ResolvedArea | null {
  const fix = readCachedFix();
  if (fix) {
    const v = opts.nearFn(fix[0], fix[1], 8);
    if (v) return { v, context: "near-you" };
  }
  for (const id of opts.suggestedIds) {
    const v = opts.verdicts.find((x) => x.id === id);
    if (v) return { v, context: "city" };
  }
  return opts.verdicts.length > 0
    ? { v: opts.verdicts[0], context: "city" }
    : null;
}

function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const la1 = (a[1] * Math.PI) / 180;
  const la2 = (b[1] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * The one signal worth leading with for this area: same suburb first
 * (highest severity, then most recent), else the nearest within 3 km —
 * always labelled with ITS suburb so we never imply a borrowed signal is
 * local. With `cityWideFallback` (used when the area is a city highlight,
 * not the user's location), an area with no nearby signal falls back to
 * the city's strongest current signal, again labelled with provenance.
 */
export function topSignalFor(
  v: Verdict,
  deduped: DedupedIncident[],
  cityWideFallback = false
): { item: DedupedIncident; borrowedFrom: string | null } | null {
  const bySeverityThenRecency = (x: DedupedIncident, y: DedupedIncident) =>
    y.severity - x.severity ||
    Date.parse(y.published_at) - Date.parse(x.published_at);

  const pinned = deduped.filter((d) => !d.is_city_anchored);

  // "Local" matches by id OR display name — the truth engine's suburb ids
  // ("sydney-cbd") differ from the verdict ids ("syd-cbd"), but names align.
  const vName = v.name.trim().toLowerCase();
  const isLocal = (d: DedupedIncident) =>
    d.suburb_id === v.id ||
    (d.suburb_name ?? "").trim().toLowerCase() === vName;

  const local = pinned.filter(isLocal).sort(bySeverityThenRecency);
  if (local[0]) return { item: local[0], borrowedFrom: null };

  const nearby = pinned
    .filter((d) => !isLocal(d))
    .map((d) => ({ d, km: haversineKm(d.coord, v.coord) }))
    .filter((x) => x.km <= 3)
    .sort((x, y) => x.km - y.km);
  if (nearby[0]) {
    return {
      item: nearby[0].d,
      borrowedFrom: nearby[0].d.suburb_name || "a nearby suburb",
    };
  }

  if (cityWideFallback) {
    const cityTop = [...pinned].sort(bySeverityThenRecency)[0];
    if (cityTop) {
      return {
        item: cityTop,
        borrowedFrom: cityTop.suburb_name || "another suburb",
      };
    }
  }
  return null;
}

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    #${SNAPSHOT_ID} {
      position: fixed;
      top: 96px;
      right: 14px;
      z-index: 1300;
      width: min(86vw, 330px);
      background: var(--surface-glass, rgba(20, 21, 24, 0.94));
      backdrop-filter: blur(14px);
      border: 1px solid var(--gold-a16, rgba(255, 211, 126, 0.16));
      border-radius: var(--r-card, 16px);
      box-shadow: 0 12px 36px rgba(0, 0, 0, 0.5);
      color: #f1f1f1;
      font: 13px/1.45 var(--font-body, system-ui, sans-serif);
      overflow: hidden;
      animation: hyperlocalSnapIn var(--dur-slow, 280ms) var(--ease-out, cubic-bezier(0.2, 0.8, 0.2, 1));
    }
    /* The answer needs no map: while the boot loader still owns the screen
       (body[data-booting], cleared by main-simple when the loader fades) the
       snapshot rides ABOVE it — a real place and its verdict are on screen
       before a single tile. This is the boot payoff, not chrome. */
    body[data-booting] #${SNAPSHOT_ID} { z-index: 10000; }
    @keyframes hyperlocalSnapIn {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    /* Exit choreography: the snapshot doesn't die, it hands off — a short
       fade/slide toward the verdict card's anchor so the card reads as the
       snapshot expanding. */
    #${SNAPSHOT_ID}.as-exit {
      animation: hyperlocalSnapOut var(--dur-base, 200ms) var(--ease-out, cubic-bezier(0.2, 0.8, 0.2, 1)) forwards;
      pointer-events: none;
    }
    @keyframes hyperlocalSnapOut {
      to { opacity: 0; transform: translateY(6px) scale(0.985); }
    }
    @media (prefers-reduced-motion: reduce) {
      #${SNAPSHOT_ID} { animation: none; }
      #${SNAPSHOT_ID}.as-exit { animation: none; opacity: 0; }
    }
    /* While the snapshot is up it owns the right rail — the intel-panel
       placeholder ("Tap any verdict pin…") would say the opposite of what
       the snapshot proves. */
    body[${BODY_FLAG}] #hyperlocal-intel-panel { display: none !important; }

    #${SNAPSHOT_ID} .as-body { padding: 14px 16px 12px; cursor: pointer; }
    #${SNAPSHOT_ID} .as-eyebrow {
      display: flex; align-items: center; justify-content: space-between;
      font: 700 10px/1 system-ui, sans-serif; letter-spacing: 0.14em;
      text-transform: uppercase; color: #FFD37E; margin-bottom: 8px;
    }
    #${SNAPSHOT_ID} .as-dismiss {
      background: transparent; border: 0; color: rgba(241,241,241,0.55);
      font: 16px/1 system-ui, sans-serif; cursor: pointer; padding: 2px 4px;
      border-radius: 6px;
    }
    #${SNAPSHOT_ID} .as-dismiss:hover,
    #${SNAPSHOT_ID} .as-dismiss:focus-visible { color: #fff; outline: none; background: rgba(255,255,255,0.08); }
    #${SNAPSHOT_ID} .as-name-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    #${SNAPSHOT_ID} .as-name { font: 700 18px/1.2 system-ui, sans-serif; color: #fff; }
    #${SNAPSHOT_ID} .as-pill {
      font: 800 10px/1 system-ui, sans-serif; letter-spacing: 0.08em;
      padding: 4px 8px; border-radius: 999px; text-transform: uppercase;
    }
    #${SNAPSHOT_ID} .as-tail { margin-top: 3px; color: rgba(241,241,241,0.6); font-size: 12px; }
    #${SNAPSHOT_ID} .as-confidence {
      margin-top: 2px; font-size: 10.5px; color: rgba(241,241,241,0.45);
      font-variant-numeric: tabular-nums;
    }
    #${SNAPSHOT_ID} .as-reason { margin-top: 8px; color: #e8e6e1; }
    #${SNAPSHOT_ID} .as-signal {
      margin: 12px -16px 0; padding: 10px 16px 0;
      border-top: 1px solid rgba(255,255,255,0.08);
    }
    #${SNAPSHOT_ID} .as-signal-eyebrow {
      font: 700 9.5px/1 system-ui, sans-serif; letter-spacing: 0.14em;
      text-transform: uppercase; color: rgba(241,241,241,0.45); margin-bottom: 6px;
    }
    #${SNAPSHOT_ID} .as-signal a {
      color: #e8e6e1; text-decoration: none; display: block;
    }
    #${SNAPSHOT_ID} .as-signal a:hover .as-signal-headline,
    #${SNAPSHOT_ID} .as-signal a:focus-visible .as-signal-headline { text-decoration: underline; }
    #${SNAPSHOT_ID} .as-signal-headline {
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden;
    }
    #${SNAPSHOT_ID} .as-signal-dot {
      display: inline-block; width: 7px; height: 7px; border-radius: 999px;
      margin-right: 6px; vertical-align: 1px;
    }
    #${SNAPSHOT_ID} .as-signal-meta {
      margin-top: 4px; font-size: 11px; color: rgba(241,241,241,0.55);
    }
    #${SNAPSHOT_ID} .as-signal-why {
      display: block; margin-top: 5px; font-size: 12px; line-height: 1.4;
      color: rgba(241,241,241,0.78);
    }
    #${SNAPSHOT_ID} .as-signal-empty { color: rgba(241,241,241,0.55); }
    #${SNAPSHOT_ID} .as-heartbeat {
      margin-top: 10px; font-size: 10.5px; color: rgba(241,241,241,0.45);
    }
    #${SNAPSHOT_ID} .as-cta {
      display: block; width: 100%; box-sizing: border-box;
      padding: 11px 16px; border: 0; cursor: pointer; text-align: center;
      background: rgba(255, 211, 126, 0.12); color: #FFD37E;
      font: 700 12px/1 system-ui, sans-serif; letter-spacing: 0.04em;
      border-top: 1px solid rgba(255, 211, 126, 0.18);
      transition: background 140ms ease;
    }
    #${SNAPSHOT_ID} .as-cta:hover, #${SNAPSHOT_ID} .as-cta:focus-visible {
      background: rgba(255, 211, 126, 0.22); outline: none;
    }
    /* Hide while any full-screen overlay owns the viewport (same body-flag
       contract the incident layer observes). */
    body[data-mobile-filters] #${SNAPSHOT_ID},
    body[data-school-chart-open] #${SNAPSHOT_ID} { display: none; }

    @media (max-width: 720px), (max-height: 500px) {
      #${SNAPSHOT_ID} {
        /* Clear the dock's two wrapped rows (~95px) + home indicator —
           the old 76px anchor sat the card ON the dock's search row.
           Height-capped with internal scroll so a landscape phone
           (844x390) still sees the whole card inside the viewport. */
        top: auto; right: 12px; left: 12px;
        bottom: calc(112px + env(safe-area-inset-bottom, 0px));
        width: auto;
        max-height: calc(100vh - 132px);
        max-height: calc(100dvh - 132px);
        overflow-y: auto;
      }
      #${SNAPSHOT_ID} .as-name { font-size: 16px; }
    }
  `;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeHref(url: string | undefined): string {
  if (!url) return "#";
  try {
    const u = new URL(url);
    if (u.protocol === "https:" || u.protocol === "http:") return u.href;
  } catch {
    /* invalid URL */
  }
  return "#";
}

export function dismissAreaSnapshot(): void {
  const el = document.getElementById(SNAPSHOT_ID);
  // A graceful exit is already in flight (expand handed off to the verdict
  // card) — let the animation finish; the element removes itself.
  if (el?.dataset.exiting === "1") {
    document.body.removeAttribute(BODY_FLAG);
    return;
  }
  el?.remove();
  document.body.removeAttribute(BODY_FLAG);
}

export function installAreaSnapshot(opts: AreaSnapshotOpts): void {
  const area = resolveArea(opts);
  if (!area) return; // no verdicts — nothing honest to show

  ensureStyles();
  dismissAreaSnapshot(); // idempotent re-install (city switch in same tab)

  const { v, context } = area;
  const lv = {
    bg: LEVEL_COLORS[v.verdict],
    fg: LEVEL_FG[v.verdict],
    tail: LEVEL_TAILS[v.verdict],
  };

  const el = document.createElement("aside");
  el.id = SNAPSHOT_ID;
  el.setAttribute("role", "region");
  el.setAttribute("aria-label", `${v.name} snapshot`);

  const eyebrow =
    context === "near-you" ? "Near you" : `${opts.cityLabel} · start here`;

  el.innerHTML = `
    <div class="as-body" tabindex="0" role="button"
         aria-label="Open the full verdict for ${escapeHtml(v.name)}">
      <div class="as-eyebrow">
        <span>${escapeHtml(eyebrow)}</span>
        <button type="button" class="as-dismiss" aria-label="Dismiss snapshot">×</button>
      </div>
      <div class="as-name-row">
        <span class="as-name">${escapeHtml(v.name)}</span>
        <span class="as-pill" style="background:${lv.bg};color:${lv.fg}">${v.verdict}</span>
      </div>
      <div class="as-tail">${escapeHtml(lv.tail)}</div>
      ${
        typeof v.confidenceScore === "number"
          ? `<div class="as-confidence">Confidence ${v.confidenceScore} · editorial baseline</div>`
          : ""
      }
      <div class="as-reason">${escapeHtml(v.reasons[0] ?? "")}</div>
      <div class="as-signal">
        <div class="as-signal-eyebrow">Live signal</div>
        <div class="as-signal-slot"><span class="as-signal-empty">Listening…</span></div>
        <div class="as-heartbeat" aria-live="polite"></div>
      </div>
    </div>
    <button type="button" class="as-cta">Full verdict →</button>
  `;

  document.body.appendChild(el);
  document.body.setAttribute(BODY_FLAG, "1");

  const expand = () => {
    // Hand-off, not death: play the exit toward the verdict card's anchor
    // while the card opens. dataset.exiting tells dismissAreaSnapshot (fired
    // by the verdict-shown event a frame later) to let the animation finish.
    el.dataset.exiting = "1";
    el.classList.add("as-exit");
    const reap = () => el.remove();
    el.addEventListener("animationend", reap, { once: true });
    window.setTimeout(reap, 300); // reduced-motion / animation-off fallback
    document.body.removeAttribute(BODY_FLAG);
    opts.onExpand(v);
  };
  el.querySelector<HTMLButtonElement>(".as-cta")?.addEventListener("click", expand);
  const body = el.querySelector<HTMLDivElement>(".as-body");
  body?.addEventListener("click", (e) => {
    // Dismiss button and the signal link handle themselves.
    const t = e.target as HTMLElement;
    if (t.closest(".as-dismiss") || t.closest("a")) return;
    expand();
  });
  body?.addEventListener("keydown", (e) => {
    // Only the card body itself opens the full verdict on Enter/Space —
    // keystrokes on inner interactive elements (the trust-breakdown
    // toggle, the dismiss button, the signal link) must NOT bubble up
    // and hijack "open full verdict".
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      expand();
    }
  });
  el.querySelector<HTMLButtonElement>(".as-dismiss")?.addEventListener(
    "click",
    () => dismissAreaSnapshot()
  );

  // The full verdict card supersedes the snapshot, however it was opened
  // (pin tap, search, shortlist, deep link).
  window.addEventListener(
    "hyperlocal:verdict-shown",
    () => dismissAreaSnapshot(),
    { once: true }
  );

  // Fill the signal row + heartbeat asynchronously — the card is already
  // useful (name, verdict, reason) while the feed loads.
  void loadIncidents(opts.cityId)
    .then((feed) => {
      const slot = el.querySelector<HTMLDivElement>(".as-signal-slot");
      const hbEl = el.querySelector<HTMLDivElement>(".as-heartbeat");
      if (!slot || !hbEl) return;

      const deduped = feed.deduped as DedupedIncident[];
      const hb = feedHeartbeat({
        generatedAt: feed.generatedAt,
        itemCount: deduped.length,
        cityLabel: opts.cityLabel,
      });
      hbEl.textContent = hb.text;

      const top = feed.ready
        ? topSignalFor(v, deduped, context === "city")
        : null;
      if (!top) {
        slot.innerHTML = `<span class="as-signal-empty">No live signals here right now</span>`;
        return;
      }
      const { item, borrowedFrom } = top;
      const dotColor =
        item.category === "positive" ? COLOR.green : "#FFA64A";
      const age = relativeAge(Date.parse(item.published_at), Date.now());
      const corroboration =
        item.reportCount > 1 ? ` · ${item.reportCount} sources agree` : "";
      const where = borrowedFrom ? ` · in ${escapeHtml(borrowedFrom)}` : "";
      // Engine-fed signals carry the trust contract — show it here too,
      // in the same voice as the tap cards. News items get the honest
      // absence line instead of silence (absence shown as absence).
      // The per-axis breakdown is TAPPABLE (not hover-only — phones
      // exist; design language §8), so it lives OUTSIDE the link.
      const trust = trustLineFor(item);
      // "What it means" — the engine's one-line context (body). This is the
      // product's differentiator; without it the signal is just a headline.
      // Trim to one tidy sentence so the snapshot stays compact.
      const whyRaw = (item.body || "").trim();
      const why = whyRaw.length > 140 ? whyRaw.slice(0, 137).trimEnd() + "…" : whyRaw;
      const whyHtml = why
        ? `<span class="as-signal-why">${escapeHtml(why)}</span>`
        : "";
      slot.innerHTML = `
        <a href="${safeHref(item.url)}" target="_blank" rel="noopener noreferrer">
          <span class="as-signal-headline"><span class="as-signal-dot" style="background:${dotColor}"></span>${escapeHtml(item.headline)}</span>
          <span class="as-signal-meta">${escapeHtml(item.source)} · ${escapeHtml(age)}${corroboration}${where}</span>
        </a>
        ${whyHtml}
      `;
      if (trust) {
        const color = trust.disputed ? COLOR.trustDisputed : COLOR.trustOk;
        if (trust.title) {
          const toggle = document.createElement("button");
          toggle.type = "button";
          toggle.className = "as-signal-meta as-trust-toggle";
          toggle.style.cssText = `display:block;width:100%;text-align:left;background:none;border:0;padding:0;cursor:pointer;font:inherit;color:${color};`;
          toggle.setAttribute("aria-expanded", "false");
          toggle.textContent = `${trust.text} ▸`;
          const detail = document.createElement("span");
          detail.className = "as-signal-meta";
          detail.style.cssText = "display:none;color:rgba(241,241,241,0.65);padding-top:2px;";
          detail.textContent = trust.title;
          toggle.addEventListener("click", (e) => {
            e.stopPropagation();
            const open = detail.style.display === "none";
            detail.style.display = open ? "block" : "none";
            toggle.setAttribute("aria-expanded", String(open));
            toggle.textContent = `${trust.text} ${open ? "▾" : "▸"}`;
          });
          slot.appendChild(toggle);
          slot.appendChild(detail);
        } else {
          const line = document.createElement("span");
          line.className = "as-signal-meta";
          line.style.cssText = `display:block;color:${color};`;
          line.textContent = trust.text;
          slot.appendChild(line);
        }
      } else {
        const line = document.createElement("span");
        line.className = "as-signal-meta";
        line.style.display = "block";
        line.textContent = "News report — not yet engine-verified";
        slot.appendChild(line);
      }
    })
    .catch(() => {
      const slot = el.querySelector<HTMLDivElement>(".as-signal-slot");
      if (slot) {
        slot.innerHTML = `<span class="as-signal-empty">Couldn't reach the signal feed</span>`;
      }
      // The heartbeat must not sit silently empty on failure — unknown
      // freshness is itself a fact worth stating (never silently fresh).
      const hbEl = el.querySelector<HTMLDivElement>(".as-heartbeat");
      if (hbEl) hbEl.textContent = "Freshness unknown right now";
    });
}
